import React, { useState, useEffect, useMemo } from "react";
import {
    Card,
    CardBody,
    Col,
    Container,
    Row,
    Button,
    Table,
    Badge,
} from "reactstrap";
import { ClipLoader } from "react-spinners";
import Swal from "sweetalert2";
import { Download, Users, Wallet, TrendingUp, Calendar, Search, Award, Activity, UserMinus, UserCheck, AlertCircle } from "lucide-react";
import {
    hasPagePermissionWithAdmin,
    isUserAdmin
} from "../../components/Permission/PermissionService";
import Breadcrumb from "../../components/Common/Breadcrumb";
import { getCustomerSegregationReport, exportCustomerSegregationReportToExcel } from "../../components/Api/GmsCustomerSegregationReport";
import CustomerProfileModal from "../../components/CustomerProfileModal";

// Format currency
const formatCurrency = (amount) => {
    return new Intl.NumberFormat('en-US', {
        minimumFractionDigits: 2,
        maximumFractionDigits: 2
    }).format(amount || 0);
};

// Format date for display
const formatDate = (dateString) => {
    if (!dateString) return "N/A";
    try {
        const date = new Date(dateString);
        if (isNaN(date.getTime())) return "N/A";
        const day = date.getDate().toString().padStart(2, '0');
        const month = (date.getMonth() + 1).toString().padStart(2, '0');
        const year = date.getFullYear();
        return `${day}/${month}/${year}`;
    } catch (error) {
        return "N/A";
    }
};

const getSegmentColor = (segment) => {
    if (segment.includes("VIP")) return "primary";
    if (segment.includes("Repeat")) return "success";
    if (segment.includes("Recent")) return "info";
    if (segment.includes("One-Time")) return "warning";
    if (segment.includes("Sleeping")) return "dark";
    if (segment.includes("Lost")) return "danger";
    return "secondary";
};

const getSegmentIcon = (segment, size = 18) => {
    if (segment.includes("VIP")) return <Award size={size} className="me-2 text-primary" />;
    if (segment.includes("Repeat")) return <TrendingUp size={size} className="me-2 text-success" />;
    if (segment.includes("Recent")) return <UserCheck size={size} className="me-2 text-info" />;
    if (segment.includes("One-Time")) return <Activity size={size} className="me-2 text-warning" />;
    if (segment.includes("Sleeping")) return <AlertCircle size={size} className="me-2 text-dark" />;
    if (segment.includes("Lost")) return <UserMinus size={size} className="me-2 text-danger" />;
    return <Users size={size} className="me-2 text-secondary" />;
};

const SEGMENTS_LIST = [
    "High Value (VIP)",
    "Repeat",
    "Recent / New",
    "One-Time",
    "Sleeping",
    "Lost"
];

const getUserSession = () => {
    const authUser = JSON.parse(localStorage.getItem("authUser") || "{}");
    const user = JSON.parse(localStorage.getItem("user") || "{}");
    return {
        businessId: authUser.businessId || user.user_Business_Id || null,
        branchId: authUser.branchId || user.user_Branch_Id || null,
    };
};

export default function CustomerSegregationReportPage() {
    const [loading, setLoading] = useState(false);
    const [exportLoading, setExportLoading] = useState(false);
    const [reportData, setReportData] = useState({ summary: [], details: [] });
    const [showCustomerProfile, setShowCustomerProfile] = useState(false);
    const [selectedCustomerId, setSelectedCustomerId] = useState(null);
    const [searchTerm, setSearchTerm] = useState("");
    const [selectedSegment, setSelectedSegment] = useState(null);

    // Filtered Summary ensures all 6 are shown
    const summaryToDisplay = useMemo(() => {
        return SEGMENTS_LIST.map(segmentName => {
            const data = reportData.summary.find(s => s.segment === segmentName);
            return {
                segment: segmentName,
                customerCount: data ? data.customerCount : 0,
                totalRevenue: data ? data.totalRevenue : 0
            };
        });
    }, [reportData.summary]);

    // Check permissions
    const permissions = JSON.parse(localStorage.getItem('permissions') || '[]');
    const hasPermission = hasPagePermissionWithAdmin(permissions, '/GmsCustomerSegregationReport');

    const session = useMemo(() => getUserSession(), []);

    useEffect(() => {
        if (hasPermission) {
            loadReport();
        } else {
            window.location.href = '/Dashboard';
        }
    }, [hasPermission]);

    const loadReport = async () => {
        try {
            setLoading(true);
            const data = await getCustomerSegregationReport(session.businessId, session.branchId);
            setReportData(data);
        } catch (error) {
            console.error("Error loading report:", error);
            Swal.fire("Error", "Failed to load report data", "error");
        } finally {
            setLoading(false);
        }
    };

    const handleExport = async () => {
        try {
            setExportLoading(true);
            await exportCustomerSegregationReportToExcel(session.businessId, session.branchId);
        } catch (error) {
            console.error("Error exporting report:", error);
            Swal.fire("Error", "Failed to export report", "error");
        } finally {
            setExportLoading(false);
        }
    };

    const filteredDetails = useMemo(() => {
        if (!selectedSegment) return [];

        return reportData.details.filter(item => {
            const matchesSearch = item.customer_NameEn.toLowerCase().includes(searchTerm.toLowerCase());
            const matchesSegment = selectedSegment === "All" || item.segment === selectedSegment;
            return matchesSearch && matchesSegment;
        });
    }, [reportData.details, searchTerm, selectedSegment]);

    if (!hasPermission) return null;

    return (
        <div className="page-content">
            <Container fluid>
                <Breadcrumb title="Reports" breadcrumbItem="Customer Segregation Report" />

                <Row className="mb-4">
                    <Col>
                        <div className="d-flex align-items-center justify-content-end gap-2">
                            <Button color="success" onClick={handleExport} disabled={exportLoading || loading}>
                                {exportLoading ? <ClipLoader size={16} color="#fff" /> : <Download size={18} className="me-2" />}
                                Export to Excel
                            </Button>
                            <Button color="primary" onClick={loadReport} disabled={loading}>
                                {loading ? <ClipLoader size={16} color="#fff" /> : <Activity size={18} className="me-2" />}
                                Refresh Report
                            </Button>
                        </div>
                    </Col>
                </Row>

                {/* Summary Cards */}
                <Row>
                    {summaryToDisplay.map((sum, index) => (
                        <Col xl={3} md={6} key={index} className="mb-4">
                            <Card
                                className={`border-0 shadow-sm h-100 ${selectedSegment === sum.segment ? 'border border-primary shadow-lg' : ''}`}
                                style={{
                                    cursor: 'pointer',
                                    transition: 'all 0.2s ease',
                                    transform: selectedSegment === sum.segment ? 'scale(1.03)' : 'none',
                                    backgroundColor: selectedSegment === sum.segment ? '#fbfcfe' : 'white'
                                }}
                                onClick={() => setSelectedSegment(selectedSegment === sum.segment ? null : sum.segment)}
                            >
                                <CardBody>
                                    <div className="d-flex align-items-center mb-3">
                                        <div className={`avatar-sm rounded-circle bg-${getSegmentColor(sum.segment)} bg-soft text-center d-flex align-items-center justify-content-center`}>
                                            {getSegmentIcon(sum.segment, 24)}
                                        </div>
                                        <div className="ms-3">
                                            <h5 className="mb-0 text-truncate" style={{ maxWidth: '150px' }}>{sum.segment}</h5>
                                        </div>
                                    </div>
                                    <div className="row text-center">
                                        <div className="col-6 border-end">
                                            <p className="text-muted mb-1 font-size-12">Customers</p>
                                            <h4 className="mb-0">{sum.customerCount}</h4>
                                        </div>
                                        <div className="col-6">
                                            <p className="text-muted mb-1 font-size-12">Revenue</p>
                                            <h4 className="mb-0 text-success">{formatCurrency(sum.totalRevenue)}</h4>
                                        </div>
                                    </div>
                                </CardBody>
                            </Card>
                        </Col>
                    ))}
                </Row>

                {/* Details Table */}
                {selectedSegment && (
                    <Card className="border-0 shadow-sm animate__animated animate__fadeInUp">
                        <CardBody>
                            <Row className="mb-4 align-items-center">
                                <Col md={4}>
                                    <h5 className="card-title mb-0">Customer list <Badge color={getSegmentColor(selectedSegment)} className="ms-2">{selectedSegment}</Badge></h5>
                                </Col>
                                <Col md={8}>
                                    <div className="d-flex gap-2 justify-content-end">
                                        <div className="search-box">
                                            <div className="position-relative">
                                                <input
                                                    type="text"
                                                    className="form-control rounded-pill"
                                                    placeholder="Search customer name..."
                                                    value={searchTerm}
                                                    onChange={(e) => setSearchTerm(e.target.value)}
                                                    style={{ paddingLeft: '40px' }}
                                                />
                                                <Search className="position-absolute text-muted" size={18} style={{ left: '15px', top: '10px' }} />
                                            </div>
                                        </div>
                                        <Button color="light" size="sm" onClick={() => setSelectedSegment(null)}>Hide List</Button>
                                    </div>
                                </Col>
                            </Row>

                            <div className="table-responsive">
                                <Table hover className="align-middle table-nowrap mb-0">
                                    <thead className="table-light">
                                        <tr>
                                            <th>Customer Name</th>
                                            <th>Segment</th>
                                            <th className="text-end">Visit Count</th>
                                            <th className="text-end">Lifetime Spend</th>
                                            <th>Last Visit</th>
                                            <th className="text-center">Days Since</th>
                                        </tr>
                                    </thead>
                                    <tbody>
                                        {loading ? (
                                            <tr>
                                                <td colSpan="6" className="text-center py-5">
                                                    <ClipLoader color="#556ee6" />
                                                    <p className="mt-2 text-muted">Calculating segments...</p>
                                                </td>
                                            </tr>
                                        ) : filteredDetails.length > 0 ? (
                                            filteredDetails.map((item, index) => (
                                                <tr key={index}>
                                                    <td>
                                                        <div className="d-flex align-items-center">
                                                            <div className="avatar-xs me-3">
                                                                <span className="avatar-title rounded-circle bg-primary bg-soft text-primary font-size-12">
                                                                    {item.customer_NameEn.charAt(0)}
                                                                </span>
                                                            </div>
                                                            <div>
                                                                <h5 className="font-size-14 mb-1">
                                                                    <a
                                                                        href="#"
                                                                        onClick={(e) => { e.preventDefault(); setSelectedCustomerId(item.customer_Id); setShowCustomerProfile(true); }}
                                                                        className="text-dark"
                                                                    >
                                                                        {item.customer_NameEn}
                                                                    </a>
                                                                </h5>
                                                                <p className="text-muted mb-0 font-size-12">{item.customer_ContactNo}</p>
                                                            </div>
                                                        </div>
                                                    </td>
                                                    <td>
                                                        <Badge
                                                            color={getSegmentColor(item.segment)}
                                                            pill
                                                            className="font-size-11 px-2 py-1 text-dark"
                                                            style={{ color: '#000' }}
                                                        >
                                                            {item.segment}
                                                        </Badge>
                                                    </td>
                                                    <td className="text-end font-weight-bold">{item.visitCount}</td>
                                                    <td className="text-end text-success font-weight-bold">{formatCurrency(item.lifetimeSpend)}</td>
                                                    <td>{formatDate(item.lastVisitDate)}</td>
                                                    <td className="text-center">
                                                        <span className={item.daysSinceLastVisit > 120 ? "text-danger" : item.daysSinceLastVisit > 60 ? "text-warning" : "text-success"}>
                                                            {item.daysSinceLastVisit} days
                                                        </span>
                                                    </td>
                                                </tr>
                                            ))
                                        ) : (
                                            <tr>
                                                <td colSpan="6" className="text-center py-5">
                                                    <div className="text-muted">No customers found matching the criteria.</div>
                                                </td>
                                            </tr>
                                        )}
                                    </tbody>
                                </Table>
                            </div>
                        </CardBody>
                    </Card>
                )}

                {/* Customer Profile Modal */}
                <CustomerProfileModal
                    isOpen={showCustomerProfile}
                    toggle={() => {
                        setShowCustomerProfile(false);
                        setSelectedCustomerId(null);
                    }}
                    customerId={selectedCustomerId}
                />
            </Container>
        </div>
    );
}
